import { useDialog } from 'muibox';
import { useContext } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';

export interface MetricCrossSubscriberEarningsResult {
    _id: string;
    total: object;
    totalNet: object;
    totalBefore: object;
    totalBeforeNet: object;
    totalWithoutSubscription: object;
    totalNetWithoutSubscription: object;
    totalBeforeWithoutSubscription: object;
    totalBeforeNetWithoutSubscription: object;
}

export type FetchMetricCrossReferenceEarningsDeps = {
    axios: ReturnType<typeof useAxios>;
    metricsBase: string;
    userId: string;
    dialog: ReturnType<typeof useDialog>;
};

export const fetchMetricCrossReferenceEarnings = async (
    deps: FetchMetricCrossReferenceEarningsDeps,
    metricId: string,
    type: 'campaign' | 'trial',
    crossReferenceSubscriberId: string,
    signal?: AbortSignal,
): Promise<MetricCrossSubscriberEarningsResult | null> => {
    const query: string = `${deps.metricsBase}${deps.userId}/${
        type === 'campaign' ? 'promocampaigns' : 'trials'
    }/${metricId}/crossEarnings/${crossReferenceSubscriberId !== 'all' ? crossReferenceSubscriberId : 'all'}`;

    const data = await deps.axios
        .get(query, { signal })
        .then(async response => response.data)
        .catch(error => {
            if (error.name === 'CanceledError' || error.name === 'AbortError') {
                throw error;
            }

            console.error(error);
            handleHttpError(error, deps.dialog);

            return null;
        });

    return data;
};

const useSextforceMetricsCrossReferenceMetricEarnings = (
    metricId: string,
    type: 'campaign' | 'trial',
    crossReferenceSubscriberId: string,
) => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const dialog = useDialog();
    const params = useParams();
    const axios = useAxios();

    const fetchDeps: FetchMetricCrossReferenceEarningsDeps = {
        axios,
        metricsBase: settingsContext.routes.metrics.base,
        userId: params.userId as string,
        dialog,
    };

    return useQuery(
        ['metricsCrossReferenceMetricEarnings', userContext.jwtToken, params.userId, metricId, crossReferenceSubscriberId],
        () => fetchMetricCrossReferenceEarnings(fetchDeps, metricId, type, crossReferenceSubscriberId),
        {
            refetchOnWindowFocus: false,
            // 5 minutes
            staleTime: 1000 * 60 * 5,
            enabled:
                userContext.jwtToken && settingsContext.apiKey && params.userId && metricId !== '' && crossReferenceSubscriberId !== ''
                    ? true
                    : false,
        },
    );
};

export default useSextforceMetricsCrossReferenceMetricEarnings;
